/* eslint-disable max-lines */

'use client'

import { ReactNode, useMemo } from 'react'
import { useMutationState } from '@tanstack/react-query'
import classNames from 'classnames'
import { useOurStore } from '@/app/_providers/StoreProvider'
import { schema } from '@/app/_components/CustomerInfoForm/CustomerInfoForm.schema'
import { MUTATION_KEYS } from '@/config/tanstack-querykeys.config'
import s from './DebugPanel.module.css'
import { extractValidationErrors } from '@/utils/errors.utils'

const CustomerInfoDebug = (): ReactNode => {
  const formValues = useOurStore((state) => state.formValues)
  const userIds = useOurStore((state) => state.userIds)
  const userIsLocal = useOurStore((state) => state.userIsLocal)
  const giftRecipient = useOurStore((state) => state.giftRecipient)

  const isGiftOrder = formValues.giftOrder === 'true'

  const giftRecipientMutations = useMutationState({
    filters: { mutationKey: [MUTATION_KEYS.CREATE_GIFT_RECIPIENT] },
    select: (mutation) => ({
      status: mutation.state.status,
      data: mutation.state.data as Transformed.CreateUserResponse | undefined,
      error: mutation.state.error,
      submittedAt: mutation.state.submittedAt,
    }),
  })
  const lastGiftMutation = giftRecipientMutations[giftRecipientMutations.length - 1] ?? null

  const validationErrors = useMemo(() => {
    try {
      schema.validateSync(formValues, { abortEarly: false })
      return {}
    } catch (err: unknown) {
      return extractValidationErrors(err)
    }
  }, [formValues])

  const isValid = Object.keys(validationErrors).length === 0

  const isCreated = !!giftRecipient.giftRecipientId
  const mutationStatus = lastGiftMutation?.status

  const statusLabel = isCreated ? 'created' : (mutationStatus ?? 'idle')
  return (
    <details className={s.panel} aria-label="Customer info debug panel">
      <summary className={s.summary}>
        <span className={s.summaryIcon} aria-hidden="true">&#9654;</span>
        <span className={s.summaryTitle}>Customer Info Debug</span>
        <span className={`${s.summaryBadge} ${isValid ? s.summaryBadgeOk : s.summaryBadgeError}`}>
          {isValid ? 'valid' : 'invalid'}
        </span>
      </summary>

      <div className={s.body}>
        {/* ── User IDs ────────────────────────────────────── */}
        <details className={s.section} open>
          <summary className={s.sectionSummary}>
            <span className={s.sectionIcon} aria-hidden="true">&#9654;</span>
            <span className={s.sectionTitle}>User IDs</span>
          </summary>
          <div className={s.sectionBody}>
            <div className={s.row}>
              <span className={s.key}>uid</span>
              <span className={userIds.uid ? s.valString : s.valNull}>
                {userIds.uid ?? 'null'}
              </span>
            </div>
            <div className={s.row}>
              <span className={s.key}>iid</span>
              <span className={userIds.iid ? s.valString : s.valNull}>
                {userIds.iid ?? 'null'}
              </span>
            </div>
            <div className={s.row}>
              <span className={s.key}>userIsLocal</span>
              <span className={s.valNumber}>{String(userIsLocal)}</span>
            </div>
          </div>
        </details>

        {/* ── Form Values ─────────────────────────────────── */}
        <details className={s.section} open>
          <summary className={s.sectionSummary}>
            <span className={s.sectionIcon} aria-hidden="true">&#9654;</span>
            <span className={s.sectionTitle}>Form Values</span>
          </summary>
          <div className={s.sectionBody}>
            <div className={s.row}>
              <span className={s.key}>firstName</span>
              <span className={formValues.firstName ? s.valString : s.valNull}>
                {formValues.firstName || 'empty'}
              </span>
            </div>
            <div className={s.row}>
              <span className={s.key}>lastName</span>
              <span className={formValues.lastName ? s.valString : s.valNull}>
                {formValues.lastName || 'empty'}
              </span>
            </div>
            <div className={s.row}>
              <span className={s.key}>email</span>
              <span className={formValues.email ? s.valString : s.valNull}>
                {formValues.email || 'empty'}
              </span>
            </div>
            <div className={s.row}>
              <span className={s.key}>emailConfirm</span>
              <span className={formValues.emailConfirm ? s.valString : s.valNull}>
                {formValues.emailConfirm || 'empty'}
              </span>
            </div>
            <div className={s.row}>
              <span className={s.key}>phoneNumber</span>
              <span className={formValues.phoneNumber ? s.valString : s.valNull}>
                {formValues.phoneNumber || 'empty'}
              </span>
            </div>
            <div className={s.row}>
              <span className={s.key}>country</span>
              <span className={s.valString}>{formValues.country}</span>
            </div>
            <div className={s.row}>
              <span className={s.key}>zipCode</span>
              <span className={formValues.zipCode ? s.valString : s.valNull}>
                {formValues.zipCode || 'empty'}
              </span>
            </div>
            <div className={s.row}>
              <span className={s.key}>emailSignup</span>
              <span className={s.val}>
                <span className={`${s.pill} ${formValues.emailSignup === 'true' ? s.pillOk : s.pillMuted}`}>
                  {formValues.emailSignup}
                </span>
              </span>
            </div>
          </div>
        </details>

        {/* ── Gift Order ──────────────────────────────────── */}
        <details className={s.section}>
          <summary className={s.sectionSummary}>
            <span className={s.sectionIcon} aria-hidden="true">&#9654;</span>
            <span className={s.sectionTitle}>Gift Order</span>
            <span className={s.val}>
              <span className={`${s.pill} ${isGiftOrder ? s.pillOk : s.pillMuted}`}>
                {isGiftOrder ? 'yes' : 'no'}
              </span>
            </span>
          </summary>
          <div className={s.sectionBody}>
            {!isGiftOrder
              ? <p className={s.empty}>Gift order not enabled</p>
              : (
                <>
                  <div className={s.row}>
                    <span className={s.key}>giftFirstName</span>
                    <span className={formValues.giftOrderFirstName ? s.valString : s.valNull}>
                      {formValues.giftOrderFirstName || 'empty'}
                    </span>
                  </div>
                  <div className={s.row}>
                    <span className={s.key}>giftLastName</span>
                    <span className={formValues.giftOrderLastName ? s.valString : s.valNull}>
                      {formValues.giftOrderLastName || 'empty'}
                    </span>
                  </div>
                  <div className={s.row}>
                    <span className={s.key}>giftEmail</span>
                    <span className={formValues.giftOrderEmail ? s.valString : s.valNull}>
                      {formValues.giftOrderEmail || 'empty'}
                    </span>
                  </div>
                  <div className={s.row}>
                    <span className={s.key}>giftMessage</span>
                    <span className={formValues.giftOrderMessage ? s.valString : s.valNull}>
                      {formValues.giftOrderMessage || 'empty'}
                    </span>
                  </div>
                </>
              )}
          </div>
        </details>

        {/* ── Gift Recipient API ─────────────────────────── */}
        <details className={s.section}>
          <summary className={s.sectionSummary}>
            <span className={s.sectionIcon} aria-hidden="true">&#9654;</span>
            <span className={s.sectionTitle}>Gift Recipient API</span>
            <span className={s.val}>
              <span className={classNames(s.pill, {
                [s.pillOk]: isCreated,
                [s.pillError]: !isCreated && mutationStatus === 'error',
                [s.pillWarn]: !isCreated && mutationStatus === 'pending',
                [s.pillMuted]: !isCreated && !['error', 'pending'].includes(mutationStatus),
              })}
              >
                {statusLabel}
              </span>
            </span>
          </summary>
          <div className={s.sectionBody}>
            <div className={s.row}>
              <span className={s.key}>store.giftRecipientId</span>
              <span className={giftRecipient.giftRecipientId ? s.valString : s.valNull}>
                {giftRecipient.giftRecipientId || 'empty'}
              </span>
            </div>
            <div className={s.row}>
              <span className={s.key}>store.giftMessage</span>
              <span className={giftRecipient.giftMessage ? s.valString : s.valNull}>
                {giftRecipient.giftMessage ?? 'null'}
              </span>
            </div>

            {lastGiftMutation && (
              <>
                <div className={s.row}>
                  <span className={s.key}>mutation.status</span>
                  <span className={s.val}>
                    <span className={classNames(s.pill, {
                      [s.pillOk]: isCreated,
                      [s.pillError]: !isCreated && lastGiftMutation.status === 'error',
                      [s.pillWarn]: !isCreated && lastGiftMutation.status === 'pending',
                      [s.pillMuted]: !isCreated && !['error', 'pending'].includes(lastGiftMutation.status),
                    })}
                    >
                      {lastGiftMutation.status}
                    </span>
                  </span>
                </div>

                {lastGiftMutation.data && (
                  <>
                    <div className={s.row}>
                      <span className={s.key}>response.status</span>
                      <span className={lastGiftMutation.data.status === 'SUCCESS' ? s.valOk : s.valError}>
                        {lastGiftMutation.data.status}
                      </span>
                    </div>
                    {lastGiftMutation.data.status === 'SUCCESS' && lastGiftMutation.data.user && !lastGiftMutation.data.user.id && (
                      <div className={s.row}>
                        <span className={s.key}>note</span>
                        <span className={s.valWarn}>409 Conflict — existing user reused</span>
                      </div>
                    )}
                    {lastGiftMutation.data.status === 'FAILURE' && (
                      <div className={s.row}>
                        <span className={s.key}>note</span>
                        <span className={s.valError}>API error — gift recipient creation failed</span>
                      </div>
                    )}
                    {lastGiftMutation.data.user && (
                      <>
                        <div className={s.row}>
                          <span className={s.key}>response.identityId</span>
                          <span className={s.valString}>
                            {lastGiftMutation.data.user.identityId}
                          </span>
                        </div>
                        <div className={s.row}>
                          <span className={s.key}>response.userId</span>
                          <span className={s.valString}>
                            {lastGiftMutation.data.user.id}
                          </span>
                        </div>
                        <div className={s.row}>
                          <span className={s.key}>response.email</span>
                          <span className={s.valString}>
                            {lastGiftMutation.data.user.email}
                          </span>
                        </div>
                      </>
                    )}
                    {lastGiftMutation.data.message && (
                      <div className={s.row}>
                        <span className={s.key}>response.message</span>
                        <span className={s.valError}>
                          {lastGiftMutation.data.message}
                        </span>
                      </div>
                    )}
                  </>
                )}

                {lastGiftMutation.error && (
                  <div className={s.row}>
                    <span className={s.key}>error</span>
                    <span className={s.valError}>
                      {lastGiftMutation.error.message}
                    </span>
                  </div>
                )}
              </>
            )}

            {!lastGiftMutation && (
              <p className={s.empty}>No gift recipient mutation attempted</p>
            )}
          </div>
        </details>

        {/* ── Validation ──────────────────────────────────── */}
        <details className={s.section}>
          <summary className={s.sectionSummary}>
            <span className={s.sectionIcon} aria-hidden="true">&#9654;</span>
            <span className={s.sectionTitle}>Validation</span>
            <span className={s.val}>
              <span className={`${s.pill} ${isValid ? s.pillOk : s.pillError}`}>
                {isValid ? 'pass' : `${Object.keys(validationErrors).length} errors`}
              </span>
            </span>
          </summary>
          <div className={s.sectionBody}>
            {isValid
              ? <p className={s.empty}>All fields valid</p>
              : (
                <ul className={s.errorList}>
                  {Object.entries(validationErrors).map(([field, msg]) => (
                    <li key={field} className={s.errorItem}>
                      <span className={s.errorField}>{field}</span>
                      <span className={s.errorMsg}>{msg}</span>
                    </li>
                  ))}
                </ul>
              )}
          </div>
        </details>
      </div>
    </details>
  )
}

export default CustomerInfoDebug
